import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Link } from '~/techstyle-shared/next-routes';

import getUrlPath from '../utils/getUrlPath';
import useBuilderCategoryNav from '../utils/useBuilderCategoryNav';

const BreadcrumbsList = styled.ul`
  display: flex;
  gap: 4px;
  list-style: none;
`;

const BreadcrumbListItem = styled.li`
  font-size: 14px;
  font-weight: 400;

  display: flex;
  gap: 4px;

  ${({ theme }) =>
    theme.builderCategoryNav.breadcrumbs.breadCrumbListItem.default};

  ${({ $isActive, theme }) =>
    $isActive &&
    theme.builderCategoryNav.breadcrumbs.breadCrumbListItem.active};
`;

const StyledBreadcrumbLink = styled.a`
  color: ${({ theme }) => theme.colors.gridBreadcrumbTextColor};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Recursively generates a breadcrumb trail based on the provided builder JSON blocks
 * and the current page path. It traverses the JSON structure to find matching paths
 * and constructs a breadcrumb trail.
 *
 * @param {Object} params
 * @param {Array} params.builderJsonBlocks - An array of Builder JSON blocks representing the category navigation structure.
 * @param {string} params.currentPagePath - The current page path to match against the JSON blocks.
 * @param {Array} [params.breadcrumbs=[]] - An array to accumulate breadcrumb objects during recursion.
 * @returns {Array|null} - An array of breadcrumb objects with `label` and `urlPath` properties, or `null` if no breadcrumbs are found.
 *
 * @example
 * const builderJsonBlocks = [
 *   {
 *     component: {
 *       options: {
 *         linkPath: '/womens',
 *         linkText: 'Women',
 *       },
 *     },
 *     children: [
 *       {
 *         component: {
 *           options: {
 *             linkPath: '/womens/bras',
 *             linkText: 'Bras',
 *           },
 *         },
 *       },
 *     ],
 *   },
 * ];
 *
 * const currentPagePath = '/womens/bras';
 *
 * const breadcrumbs = getBreadcrumbs({
 *   builderJsonBlocks,
 *   currentPagePath,
 * });
 *
 * console.log(breadcrumbs);
 * // Output:
 * // [
 * //   { label: 'Women', urlPath: '/womens' },
 * //   { label: 'Bras', urlPath: '/womens/bras' }
 * // ]
 */
function getBreadcrumbs({
  builderJsonBlocks,
  currentPagePath: pagePath,
  breadcrumbs = [],
}) {
  if (!Array.isArray(builderJsonBlocks) || !pagePath) {
    return null;
  }

  for (const block of builderJsonBlocks) {
    const linkPath = block?.component?.options?.linkPath;
    const linkText = block?.component?.options?.linkText;

    if (linkPath && linkText) {
      const builderBlockLinkPath = getUrlPath({ url: linkPath });
      const currentPagePath = getUrlPath({ url: pagePath });

      // check if current page path starts with this block's `linkPath`.
      // an example of this is if the `builderBlockLinkPath` is `/womens`
      // and the `currentPagePath` is `/womens/bras`.
      // this means we are on a page that is a child of the `/womens` category
      // so we want to include this block in the breadcrumb trail
      // even if it's not an exact match.
      // we will continue to traverse children to find an exact match
      if (currentPagePath.startsWith(builderBlockLinkPath)) {
        const cumulativeBreadcrumbs = [
          ...breadcrumbs,
          {
            label: linkText,
            urlPath: linkPath,
          },
        ];

        // if this is an exact match, we found our breadcrumb trail as `cumulativeBreadcrumbs`
        if (currentPagePath === builderBlockLinkPath) {
          return cumulativeBreadcrumbs;
        }

        // continue traversing children if there's a partial match
        if (block.children) {
          const result = getBreadcrumbs({
            builderJsonBlocks: block.children,
            currentPagePath: pagePath,
            breadcrumbs: cumulativeBreadcrumbs,
          });

          if (result) {
            return result;
          }
        }
      }
    }

    // check children even if this block doesn't have a `linkPath`
    if (block.children) {
      const result = getBreadcrumbs({
        builderJsonBlocks: block.children,
        currentPagePath: pagePath,
        breadcrumbs,
      });

      if (result) {
        return result;
      }
    }
  }

  return null;
}

function BreadcrumbCopy({ breadcrumb, isActive }) {
  return (
    <>
      {isActive ? (
        <>{breadcrumb?.label?.toUpperCase()}</>
      ) : (
        <>
          <Link href={breadcrumb?.urlPath} passHref>
            <StyledBreadcrumbLink>
              {breadcrumb?.label?.toUpperCase()}
            </StyledBreadcrumbLink>
          </Link>
          <span>/</span>
        </>
      )}
    </>
  );
}

BreadcrumbCopy.propTypes = {
  breadcrumb: PropTypes.object.isRequired,
  isActive: PropTypes.bool.isRequired,
};

function BuilderCategoryNavBreadcrumbs() {
  const { builderJson, isLoading } = useBuilderCategoryNav();
  const router = useRouter();

  const breadcrumbs = getBreadcrumbs({
    builderJsonBlocks: builderJson?.data?.blocks,
    currentPagePath: router.asPath,
  });

  if (!breadcrumbs?.length || isLoading) {
    return null;
  }

  return (
    <BreadcrumbsList data-autotag="grid-breadcrumbs-list">
      {breadcrumbs?.map((breadcrumb, index) => {
        const isActive = index === breadcrumbs.length - 1;
        const layerIndex = index + 1;

        return (
          <BreadcrumbListItem
            key={breadcrumb?.urlPath}
            data-autotag={`grid-breadcrumb-layer-${layerIndex}`}
            $isActive={isActive}
          >
            <BreadcrumbCopy breadcrumb={breadcrumb} isActive={isActive} />
          </BreadcrumbListItem>
        );
      })}
    </BreadcrumbsList>
  );
}

BuilderCategoryNavBreadcrumbs.propTypes = {};

export default BuilderCategoryNavBreadcrumbs;
